<template lang="html">
  <v-dialog
    scrollable
    :value="true"
    max-width="700"
    persistent
  >
    <v-form
      @submit.prevent="save"
      v-model="isValid"
      ref="form"
      style="width:100%"
      class="input-form"
    >
      <v-card :class="{large: !appSmall}">
        <v-card-title
          class="headline grey lighten-2"
          primary-title
        >
          {{recordId === 'new' ? 'Create' : 'Edit'}} {{ inputType.name }}
        </v-card-title>

        <v-card-text v-if="!initialLoading" class="pt-4">
          <div
            v-for="field in fields"
            :key="field[0]"
          >
            <v-layout v-if="field[0] === 'load_dimension'">
              <v-flex xs4>
                <v-text-field
                  label="Length"
                  v-model="record.load_len"
                  @input="updatePerimeter"
                  outlined
                  :disabled="loading || readOnly"
                  :rules="[...validation.numeric]"
                  :prefix="field[2] && field[2].prefix && field[2].unit ? unitName(field[2].unit) : undefined"
                  :suffix="field[2] && !field[2].prefix && field[2].unit ? unitName(field[2].unit) : undefined"
                ></v-text-field>
              </v-flex>
              <v-flex xs4>
                <v-text-field
                  label="Width"
                  v-model="record.load_width"
                  @input="updatePerimeter"
                  outlined
                  :disabled="loading || readOnly"
                  :rules="[...validation.numeric]"
                  :prefix="field[2] && field[2].prefix && field[2].unit ? unitName(field[2].unit) : undefined"
                  :suffix="field[2] && !field[2].prefix && field[2].unit ? unitName(field[2].unit) : undefined"
                ></v-text-field>
              </v-flex>
              <v-flex xs4>
                <v-text-field
                  label="Perimeter"
                  v-model="record.load_perim"
                  outlined
                  :disabled="loading || readOnly"
                  :rules="[...validation.numeric]"
                  :prefix="field[2] && field[2].prefix && field[2].unit ? unitName(field[2].unit) : undefined"
                  :suffix="field[2] && !field[2].prefix && field[2].unit ? unitName(field[2].unit) : undefined"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-text-field
              v-else
              :label="field[1]"
              v-model="record[field[0]]"
              outlined
              :disabled="loading || readOnly"
              :rules="[...validation.maxLength(20)]"
              :prefix="field[2] && field[2].prefix && field[2].unit ? unitName(field[2].unit) : undefined"
              :suffix="field[2] && !field[2].prefix && field[2].unit ? unitName(field[2].unit) : undefined"
            ></v-text-field>
          </div>
          <v-checkbox
            v-model="record.current"
            :disabled="loading"
            label="Current"
          />
        </v-card-text>

        <v-divider></v-divider>

        <v-alert :value="true" type="error" style="width:100%" v-if="error">{{error}}</v-alert>
        <v-alert :value="true" type="error" style="width:100%" v-if="!isValid">Please fix the errors</v-alert>
        <v-alert :value="true" type="warning" style="width:100%" v-if="notComplete">Not all fields have data.  May not sync to monitoring device.</v-alert>

        <v-card-actions>
          <v-btn
            v-if="!loading && !initialLoading"
            @click="$emit('close')"
            text
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-progress-circular
            v-if="loading  || initialLoading"
            class="ma-2"
            indeterminate
            color="primary"
          ></v-progress-circular>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!loading && !initialLoading"
            color="primary"
            text
            type="submit"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<style lang="scss">
.input-form {
  .v-text-field__details {
    margin-bottom: 0;
    .v-messages {
      min-height: 0;
    }
  }
}
</style>

<style lang="scss" scoped>
.large {
  .layout {
    .flex:first-child {
      padding-right: 8px;
    }
    .flex:last-child {
      padding-left: 8px;
    }
  }
}
</style>

<script>
import { mapState, mapActions } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import ValidationMixin from '@/mixins/ValidationMixin'
import inputTypes from './inputTypes'
import round from 'lodash/round'

export default {
  mixins: [ValidationMixin],
  components: {
  },
  props: {
    recordId: {
      type: [String, Number]
    },
    inputTypeId: {
      type: String
    },
    machineId: {
      type: String
    }
  },
  data () {
    return {
      record: {},
      error: null,
      initialLoading: false,
      loading: false
    }
  },
  computed: {
    ...mapState(['user']),
    inputType () {
      return inputTypes.find(t => t.key === this.inputTypeId) || {}
    },
    fields () {
      return (this.inputType.fields || []).filter(field => !(field[2] && field[2].hideEditor))
    },
    notComplete () {
      if (this.loading || this.initialLoading) {
        return false
      }
      for (let field of (this.inputType.fields || []).filter(f => !(f[2] && f[2].pseudo))) {
        if ([null, undefined, ''].includes(this.record[field[0]])) {
          return true
        }
      }
      return false
    }
  },
  methods: {
    ...mapActions('filminfo', {
      filminfoGet: 'get',
      filminfoSave: 'save'
    }),
    ...mapActions('loadrecipe', {
      loadrecipeGet: 'get',
      loadrecipeSave: 'save'
    }),
    updatePerimeter () {
      if (this.record.load_len && this.record.load_width) {
        this.record.load_perim = round((2 * this.record.load_len) + (2 * this.record.load_width), 3)
        if (isNaN(this.record.load_perim)) {
          this.record.load_perim = ''
        }
      } else {
        this.record.load_perim = ''
      }
    },
    async save () {
      if (!this.isValid) {
        this.$refs.form.validate()
        return
      }
      this.loading = true
      this.error = null
      try {
        // For some reason the API supports POST /rest/machine/filminfo
        // but requires machine id for PUT /rest/machine/:machineId/filminfo
        // Compensating for that here...
        this.record.machine = this.record.machine || this.machineId
        this.record = await this[this.inputTypeId + 'Save']([
          this.record,
          {
            urlParams: {
              machineId: this.machineId
            }
          }
        ])
        this.$emit('close')
      } catch (e) {
        this.loading = false
        this.error = get(e, 'response.data') || e
        throw e
      }
    }
  },
  async created () {
    if (this.recordId) {
      this.initialLoading = true
      try {
        let record = {}
        if (this.recordId !== 'new') {
          record = await this[this.inputTypeId + 'Get']([
            this.recordId,
            {
              urlParams: {
                machineId: this.machineId
              }
            }
          ])
        }
        this.record = cloneDeep(record)
        this.initialLoading = false
      } catch (e) {
        this.initialLoading = false
        throw e
      }
    }
  }
}
</script>
